import { useState, useRef, useEffect } from "react";
import { useIntl } from "react-intl";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from 'moment';

import { Tooltip } from '@gpstrackit/react-controls';
import {
    Icon,
    Container,
    Grid,
    Paper,
    Typography,
    Divider,
    IconButton,
} from "@mui/material";
import { NotificationManager } from "Components/Notifications";
//Components
import DataView from 'Components/DataView';
import Filters from 'Components/Filters';
import Preferences from 'Core/containers/Preferences';

import { detectBrowser, getDateFormatted, getQuery, openLink } from 'Core/data/Helpers';

//Actions
import {
    reportsExportHistoryRequest,
    reportsExportHistoryRefreshExportLogUrlRequest,
    clearRedux,
} from 'Redux/actions';

//Style
import './index.scss';

const PREFERENCE_KEY = "pm-report-export-history-tab";

const getDefaultDateRange = () => {
    const endDate = new Date();
    const startDate = moment().subtract(30, 'days').toDate();

    return {
        startDate,
        endDate,
    };
};

const formatReportName = (name) => {
    if (!name) return '';
    // Remove "Report_" from start and "_${number}" from end
    return name.replace(/^Report_/, '').replace(/_\d+$/, '');
};

const ReportExportHistory = (props) => {
    const { height } = props;
    const { messages } = useIntl();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const tableRef = useRef(null);
    const preferencesRef = useRef(null);
    const safariTabRef = useRef(null);
    const innerHeight = window.innerHeight;
    const tableHeight = height || Math.max(innerHeight - 250, 420);

    const {
        loading,
        result,
        nextToken,
        refreshExportLogUrlLoading,
        refreshExportLogUrlResult,
        refreshExportLogUrlError,
        refreshExportLogUrlCurrentId,
    } = useSelector(state => state.reportExportHistoryRedux);
    const lang = useSelector(state => state.commonRedux.language);

    const translateStatus = (status) => {
        if (!status) return "";
        const key = `report_export_history_status_${status.toLowerCase().replace(/\s+/g, '_')}`;
        return messages[key] || status;
    };

    const formatLocalizedDate = (date) => {
        if (!date) return "";
        const d = moment(date);
        if (!d.isValid()) return "";
        return d.locale(lang || 'en').format("MMM DD YYYY h:mm:ss A");
    };

    const clearRefreshExportLogUrlState = () => {
        dispatch(clearRedux("REPORTS_EXPORT_HISTORY", {
            refreshExportLogUrlLoading: false,
            refreshExportLogUrlResult: null,
            refreshExportLogUrlError: false,
            refreshExportLogUrlCurrentId: null,
        }))
    }

    const handleDownloadExportLog = (item) => {
        if (!item?.id || !item?.outputURL) {
            NotificationManager.warning(
                messages['report_export_history_download_unavailable'],
                messages['report_export_history'],
                5000,
            );
            return;
        }

        if (detectBrowser() === 'Safari') {
            safariTabRef.current = window.open('about:blank', '_blank');
            if (safariTabRef.current) {
                safariTabRef.current.document.write('Downloading...');
            }
        }

        dispatch(reportsExportHistoryRefreshExportLogUrlRequest({
            input: {
                outputUrl: item.outputURL,
                exportLogId: item.id,
            }
        }))
    }

    const [conditions, setConditions] = useState(null)
    const [columns, setColumns] = useState([
        {
            key: "name",
            title: messages['report_export_history_report'],
            show: true,
            width: 250,
            code: "report_export_history_report",
            render: (item) => {
                return <span>{formatReportName(item?.name)}</span>
            }
        },
        {
            key: "createdOn",
            title: messages['report_export_history_created_on'],
            show: true,
            width: 250,
            code: "report_export_history_created_on",
            render: (item) => {
                return <span>{formatLocalizedDate(item?.createdOn)}</span>
            }
        },
        {
            key: "status",
            title: messages['report_export_history_status'],
            show: true,
            width: 200,
            code: "report_export_history_status",
            render: (item) => {
                return <span>{translateStatus(item?.status)}</span>
            }
        },
        {
            key: "statusUpdatedOn",
            title: messages['report_export_history_status_updated_on'],
            show: true,
            width: 250,
            code: "report_export_history_status_updated_on",
            render: (item) => {
                return <span>{formatLocalizedDate(item?.statusUpdatedOn)}</span>
            }
        },
        {
            key: "type",
            title: messages['report_export_history_type'],
            show: true,
            width: 100,
            code: "report_export_history_type",
            render: (item) => {
                return <span>{item?.type}</span>
            }
        },
        {
            key: "processedRecords",
            title: messages['report_export_history_processed_records'],
            show: true,
            width: 200,
            code: "report_export_history_processed_records",
            render: (item) => {
                return <span>{item?.processedRecords}</span>
            }
        },
        {
            key: "total",
            title: messages['report_export_history_total'],
            show: true,
            width: 100,
            code: "report_export_history_total",
            render: (item) => {
                return <span>{item?.total}</span>
            }
        },
        {
            key: "errorMessage",
            title: messages['report_export_history_error_message'],
            show: true,
            width: 250,
            code: "report_export_history_error_message",
            render: (item) => {
                return <span>{item?.errorMessage}</span>
            }
        },
        {
            key: "outputURL",
            title: messages['report_export_history_output_url'],
            show: true,
            width: 70,
            code: "report_export_history_output_url",
            render: (item) => {
                const isRefreshingRow = refreshExportLogUrlLoading && refreshExportLogUrlCurrentId === item?.id;

                return (
                    <>
                        {
                            item?.outputURL ? (
                                <div>
                                    <IconButton
                                        onClick={() => handleDownloadExportLog(item)}
                                        size="small"
                                        disabled={isRefreshingRow}
                                        style={{ color: '#1D2734' }}
                                    >
                                        <Tooltip title={messages['report_export_history_output_url']}>
                                            <Icon>{isRefreshingRow ? 'hourglass_top' : 'file_open'}</Icon>
                                        </Tooltip>
                                    </IconButton>
                                </div>
                            ) : <></>
                        }
                    </>
                )
            }
        }
    ])

    const defaultDateRange = getDefaultDateRange();
    const [startDate, setStartDate] = useState(defaultDateRange.startDate);
    const [endDate, setEndDate] = useState(defaultDateRange.endDate);
    const [beforeRangeDate, setBeforeRangeDate] = useState({
        start: defaultDateRange.startDate,
        end: defaultDateRange.endDate
    })

    const [filters, setFilters] = useState({
        startDate: defaultDateRange.startDate,
        endDate: defaultDateRange.endDate,
    })

    const [limit] = useState(50);
    const [initialPreferenceValues, setInitialPreferenceValues] = useState(null);

    const getValidDate = (value) => {
        const newValue = value ? new Date(value) : null;

        return newValue && !Number.isNaN(newValue.getTime()) ? newValue : null;
    }

    const getDateRangeFromPreferences = (preferences) => {
        const preferenceDateRange = preferences?.dateRange || preferences?.conditions?.dateRange;
        const restoredStartDate = getValidDate(preferenceDateRange?.start);
        const restoredEndDate = getValidDate(preferenceDateRange?.end);

        if (restoredStartDate && restoredEndDate) {
            return {
                startDate: restoredStartDate,
                endDate: restoredEndDate,
            };
        }

        return getDefaultDateRange();
    }

    const saveDateRangePreference = (newFilters) => {
        const oldPreferences = preferencesRef?.current?.getPreferences("Other", PREFERENCE_KEY) || {};
        const currentPreferences = {
            ...oldPreferences,
            dateRange: {
                start: newFilters?.startDate,
                end: newFilters?.endDate,
            }
        };

        preferencesRef?.current?.savePreference("Other", PREFERENCE_KEY, currentPreferences);
    }

    const onChange = (value) => {
        const { start, end } = value;
        let endDate = end ? end.setHours(23, 59, 59) : null
        onChangeStartEndDate({
            start,
            end: endDate
        })
    };

    const onChangeStartEndDate = ({ start, end }) => {
        const newStart = start && new Date(start);
        const newEnd = end && new Date(end);

        let fltrs = filters;
        fltrs.startDate = newStart;
        fltrs.endDate = newEnd;
        // Set New Date Range
        if (start && end) {
            const newDateRange = {
                start,
                end,
            };
            setBeforeRangeDate(newDateRange)
        }
        setFilters({ ...fltrs });
    }

    const onCloseSelectDate = () => {
        const { start, end } = beforeRangeDate;
        if (start && end && !endDate) {
            let fltrs = filters;
            fltrs.startDate = start;
            fltrs.endDate = end;
            setFilters({ ...fltrs });
        }
    }

    const getData = (refresh, search, suggestedFilters, firstLoad, newLimit) => {
        let currentFilters = filters
        let currentLimit = limit

        if (suggestedFilters)
            currentFilters = suggestedFilters

        if (newLimit)
            currentLimit = newLimit

        // Always include dateRange in payload
        let payload = {
            dateRange: {
                start: currentFilters?.startDate,
                end: currentFilters?.endDate
            },
            limit: currentLimit,
            nextToken: !refresh ? typeof search === undefined ? nextToken : null : null
        }

        if (refresh || typeof search === undefined) {
            dispatch(clearRedux("REPORTS_EXPORT_HISTORY", { nextToken: null }))
        }
        setConditions(payload)
        dispatch(reportsExportHistoryRequest(payload))

        if (!firstLoad) tableRef?.current?.conditionsHandler(payload)
    }

    const applyFilters = () => {
        if (filters.startDate && filters.endDate) {
            saveDateRangePreference(filters)
            getData(true);
        } else {
            NotificationManager.warning(
                messages['report_export_history_date_range'],
                messages['report_export_history'],
                5000,
            );
        }
    }

    useEffect(() => {
        return () => {
            setConditions(null)
            dispatch(clearRedux("REPORTS_EXPORT_HISTORY"))
        }
    }, [])

    useEffect(() => {
        setColumns(currentColumns => currentColumns.map(col => ({
            ...col,
            title: col.code ? messages[col.code] : col.title,
            render: col.key === "name" ? (item) => <span>{formatReportName(item?.name)}</span>
                : col.key === "createdOn" ? (item) => <span>{formatLocalizedDate(item?.createdOn)}</span>
                : col.key === "status" ? (item) => <span>{translateStatus(item?.status)}</span>
                : col.key === "statusUpdatedOn" ? (item) => <span>{formatLocalizedDate(item?.statusUpdatedOn)}</span>
                : col.render,
        })));
    }, [lang]);

    useEffect(() => {
        if (!initialPreferenceValues) {
            return;
        }

        getData(true, false, initialPreferenceValues.filters, true, initialPreferenceValues.limit)
    }, [initialPreferenceValues])

    useEffect(() => {
        if (refreshExportLogUrlLoading || !refreshExportLogUrlCurrentId) {
            return;
        }
        if (refreshExportLogUrlResult?.url) {
            if (safariTabRef.current) {
                safariTabRef.current.location.href = refreshExportLogUrlResult.url;
                safariTabRef.current = null;
            } else {
                openLink(refreshExportLogUrlResult.url, "_blank")
            }

            clearRefreshExportLogUrlState()
            return;
        }

        if (refreshExportLogUrlError) {
            if (safariTabRef.current) {
                safariTabRef.current.close();
                safariTabRef.current = null;
            }

            NotificationManager.warning(
                messages['report_export_history_download_error'],
                messages['report_export_history'],
                5000,
            );
            clearRefreshExportLogUrlState()
        }
    }, [
        refreshExportLogUrlCurrentId,
        refreshExportLogUrlError,
        refreshExportLogUrlLoading,
        refreshExportLogUrlResult,
    ])

    return (
        <Container maxWidth={false} className="container-view report-export-history-container">
            <Paper elevation={0}>
                <Preferences
                    ref={preferencesRef}
                    preferencesKey={PREFERENCE_KEY}
                    onInitialPreferences={(preferences) => {
                        const restoredFilters = getDateRangeFromPreferences(preferences);
                        const restoredLimit = preferences?.rows ? Number(preferences.rows) : limit;

                        setStartDate(restoredFilters.startDate)
                        setEndDate(restoredFilters.endDate)
                        setBeforeRangeDate({
                            start: restoredFilters.startDate,
                            end: restoredFilters.endDate,
                        })
                        setFilters({ ...restoredFilters })
                        setInitialPreferenceValues({
                            filters: restoredFilters,
                            limit: restoredLimit,
                        })
                    }}
                >
                    <Grid
                        container
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                        style={{ padding: "14px 18px", borderBottom: "1px solid #e8e8e8" }}
                    >
                        <Grid item xs={12}>
                            <Typography variant="h5" gutterBottom component="div" style={{ display: 'flex', alignItems: 'center' }}>
                                <IconButton onClick={() => navigate('/app/reports/list')} size="large">
                                    <Icon style={{ cursor: "pointer" }} className="toolbar-list-icon">
                                        arrow_back
                                    </Icon>
                                </IconButton>
                                {messages['report_export_history']}
                            </Typography>
                        </Grid>
                        <div style={{ width: '100%', marginBottom: 10, marginTop: 5 }}>
                            <Divider style={{ backgroundColor: '#999999' }} />
                        </div>
                        <DataView
                            name={PREFERENCE_KEY}
                            exportName="pmreportexporthistory"
                            externalLoading={loading}
                            externalData={result?.length ? result : []}
                            ref={tableRef}
                            columns={columns}
                            height={tableHeight}
                            noSplit
                            noPDF
                            isDynamo
                            infiniteScroll
                            disableItemSelection
                            noFilters
                            noSearch
                            noTags
                            noExcel
                            nextToken={nextToken}
                            onChangeColumns={(items) => setColumns(items)}
                            extraFilters={() => {
                                return (
                                    <div className="scheduled-report-filters" style={{ width: "100%", display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                        <div className="d-flex">
                                            <div>
                                                <Filters
                                                    onChangeFilters={() => applyFilters()}
                                                    startDate={startDate}
                                                    endDate={endDate}
                                                    setStartDate={setStartDate}
                                                    setEndDate={setEndDate}
                                                    setBeforeRangeDate={setBeforeRangeDate}
                                                    beforeRangeDate={beforeRangeDate}
                                                    onChange={onChange}
                                                    onChangeStartEndDate={onChangeStartEndDate}
                                                    type="range"
                                                    hideTypeSelection
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )
                            }}
                            onChangePage={() => getData()}
                            query={getQuery("report-export-history")}
                            conditions={conditions}
                            tableButtons={['orderColumn']}
                            externalRefresh={() => getData(true)}
                            onChangeConditions={(tags, newConditions, total, newColumns, rows, sort, key, isInitial) => {
                                if (isInitial) {
                                    return;
                                }

                                let fltrs = filters
                                let currentLimit = limit
                                if (newConditions?.dateRange) {
                                    fltrs = {
                                        startDate: new Date(newConditions?.dateRange?.start),
                                        endDate: new Date(newConditions?.dateRange?.end),
                                    }
                                    setStartDate(fltrs.startDate)
                                    setEndDate(fltrs.endDate)
                                    setBeforeRangeDate({
                                        start: fltrs.startDate,
                                        end: fltrs.endDate,
                                    })
                                    setFilters({ ...fltrs })
                                }

                                if (rows) {
                                    currentLimit = Number(rows)
                                }

                                getData(false, false, fltrs, true, currentLimit);
                            }}

                        />
                    </Grid>
                </Preferences>
            </Paper>
        </Container>
    )
}

export default ReportExportHistory;
